import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, AfterViewInit, Input, ElementRef } from '@angular/core';
import { ModalController, ItemReorderEventDetail } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Utils } from 'src/app/api/helper/utils';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { MyApp } from 'src/app/api/helper/app';
import { AuftragListItem } from 'src/app/api/model/model';
import { AuftragService } from 'src/app/api/auftrag.service';
import L, { Map, TileLayer, Marker, MarkerOptions, Icon, LatLngBounds, LatLng, Polyline, DivIcon, MapOptions, Browser } from 'leaflet';
import { SystemService } from 'src/app/api/system.service';
import { Router } from '@angular/router';
import { Auftrag, GpsPosition } from 'src/app/api/model/swagger-model';

const log = new Logger('KarteWidgetComponent');

@Component({
    selector: 'app-karte-widget',
    templateUrl: './karte-widget.component.html',
    styleUrls: ['./karte-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KarteWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
    private subscriptions: Subscription[] = [];

    app = MyApp.current;

    @Input() auftraege: (AuftragListItem | Auftrag)[];
    @Input() small = false;
    @Input() locationOnly = false;
    @Input() mapHeight = '';

    isVisible = false;

    mapId = 'map-' + Utils.randomStr();

    map: L.Map;
    markers: L.Marker[] = [];
    lines: any[] = [];
    letzteEigenePosition: GpsPosition;

    constructor(
        private auftragService: AuftragService,
        private systemService: SystemService,
        private router: Router,
        private modalController: ModalController) {

    }

    async ngOnInit() {
        log?.debug('ngOnInit start');

        this.isVisible = true;

        const intervalId = setInterval(async () => {
            try {
                if (!this.isVisible) {
                    clearInterval(intervalId);
                    return;
                }

                const aktuellePosition = await this.systemService.getAktuelleGpsPosition('Karte', 60);

                if (aktuellePosition?.Latitude) {
                    let lat = aktuellePosition.Latitude;
                    let lng = aktuellePosition.Longitude;

                    let lat2 = this.letzteEigenePosition?.Latitude;
                    let lng2 = this.letzteEigenePosition?.Longitude;

                    if (!this.letzteEigenePosition || Math.abs(lat2 - lat) > 0.0001 || Math.abs(lng2 - lng) > 0.0001) {
                        this.letzteEigenePosition = aktuellePosition;

                        log?.debug('Eigene Position aktualisiert: ' + lat + ', ' + lng);

                        this.updateMap(false);
                    }
                }

            } catch (err) {
                log.warn('Error in interval: ' + Utils.getErrorMessage(err));
            }
        }, 60000);
    }

    async ngAfterViewInit() {
        // this.cdr.detach();

        await Utils.delay(200);

        this.initMap();
        this.updateMap();
    }

    async initMap() {
        const mapRef = document.getElementById(this.mapId) as HTMLElement;

        if (!mapRef) {
            log.warn('Map element not found: ' + this.mapId);
            return;
        }

        var mapOptions: MapOptions = {
        }

        if (this.small) {
            // Setze "dragging" auf false, damit die Karte nicht verschoben werden kann. Im Auftrag möchte man einfach nach unten scrollen können.
            // Es kann aber immer noch mit zwei Fingern ein pinch-zoom durchgeführt werden.
            // siehe: https://stackoverflow.com/questions/41622980/how-to-customize-touch-interaction-on-leaflet-maps
            mapOptions.dragging = Browser.mobile;
        }

        this.map = new Map(this.mapId, mapOptions);

        this.map.setView([48.99, 10.39], 9);

        const tiles = new TileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(this.map);

    }

    async updateMap(fitToBounds = true) {
        if (!this.map) {
            log.warn('updateMap: Map not initialized');
            return;
        }

        try {
            this.markers.forEach(marker => marker.remove());
            this.markers = [];

            this.lines.forEach(line => line.remove());
            this.lines = [];

            let positions: LatLng[] = [];

            let nummer = 1;

            if (this.auftraege?.length) {
                for (const auftrag of this.auftraege) {
                    if (auftrag.Abholadresse) {
                        let lat = auftrag.Abholadresse.GeoY;
                        let lng = auftrag.Abholadresse.GeoX;

                        if (!lat || !lng) {
                            lat = auftrag.Abholadresse.GeocodingLatitude;
                            lng = auftrag.Abholadresse.GeocodingLongitude;
                        }

                        if (lat && lng) {
                            let markerOptions: MarkerOptions;

                            if (this.locationOnly) {
                                // Es soll nur die Position eines Auftrags anezeigt werden und keine Reihenfolge
                                // Wird in der Adresse-Komponente verwendet
                                markerOptions = {
                                    draggable: false,
                                    title: `Auftrag: ${auftrag.Auftragsnummer}\nAbholadresse: ${auftrag.Abholadresse.Strasse}`,
                                    icon: new Icon({
                                        iconUrl: 'assets/icon/marker64.png',
                                        iconSize: [32, 32],      // Breite, Höhe in px
                                        iconAnchor: [16, 32],    // "Fußpunkt" des Icons
                                        popupAnchor: [0, -32]    // Position der Popup-Blase
                                    })
                                }
                            } else {
                                const label = nummer.toString(); // .padStart(2, '0'); // Nummerierung mit führenden Nullen

                                let cssStyle = '';

                                if(auftrag.Farbe) {
                                    cssStyle = `border-color: ${auftrag.Farbe}; color: ${auftrag.Farbe};`;
                                }

                                markerOptions = {
                                    draggable: false,
                                    title: `Auftrag: ${auftrag.Auftragsnummer}\nAbholadresse: ${auftrag.Abholadresse.Strasse}`,
                                    icon: new DivIcon({
                                        iconUrl: 'assets/icon/marker64.png',
                                        html: `<div class="auftrag-marker" style="${cssStyle}">${label}</div>`,
                                        className: '', // Verhindert Standard-Styling
                                        iconSize: [32, 32],      // Breite, Höhe in px
                                        iconAnchor: [16, 16],    // "Fußpunkt" des Icons
                                        popupAnchor: [0, -16]    // Position der Popup-Blase
                                    })
                                }
                            }

                            nummer++;

                            positions.push(new LatLng(lat, lng));

                            const marker = new Marker([lat, lng], markerOptions).addTo(this.map);

                            let popupText = `${auftrag.Abholadresse.Name1}<br />${auftrag.Abholadresse.PLZ} ${auftrag.Abholadresse.Ort}<br />${auftrag.Abholadresse.Strasse}<br />`;

                            if (auftrag.Info1) {
                                popupText += `<br />${auftrag.Info1}`;
                            }

                            if (auftrag.Info2) {
                                popupText += `<br />${auftrag.Info2}`;
                            }

                            if (auftrag.Info3) {
                                popupText += `<br />${auftrag.Info3}`;
                            }

                            popupText += '<button class="popup-button" id="detailsBtn">Details anzeigen</button>';

                            if (!this.locationOnly) {
                                marker.bindPopup(popupText, {
                                    minWidth: 150,
                                    maxWidth: 250
                                });

                                marker.on('popupopen', () => {
                                    const btn = document.getElementById('detailsBtn');

                                    if (btn) {
                                        btn.addEventListener('click', () => {
                                            // Popup schließen.
                                            marker.closePopup();

                                            // Zum Auftrag navigieren
                                            const routerLink = '/auftragsliste/details/' + auftrag.Key;
                                            this.router.navigateByUrl(routerLink);
                                        });
                                    }
                                });
                            }

                            this.markers.push(marker);
                        }
                    }
                }
            }

            // Eigene Position hinzufügen
            let eigenePosition = await this.systemService.getAktuelleGpsPosition('Karte', 60);

            // let eigenePosition = this.systemService.letzteGpsPosition;

            if (!eigenePosition) {
                if (!MyApp.isCordovaAvailable()) {
                    eigenePosition = {
                        Latitude: 48.40066, // Beispielkoordinaten
                        Longitude: 9.985675
                    };
                }
            }

            this.letzteEigenePosition = eigenePosition;

            if (eigenePosition?.Latitude) {
                const lat = eigenePosition.Latitude;
                const lng = eigenePosition.Longitude;

                if (lat && lng) {
                    const markerOptions: MarkerOptions = {
                        draggable: false,
                        icon: new Icon({
                            iconUrl: 'assets/icon/truck-rot.png',
                            iconSize: [32, 32],      // Breite, Höhe in px
                            iconAnchor: [16, 16],    // "Fußpunkt" des Icons
                            popupAnchor: [0, -16]    // Position der Popup-Blase
                        })
                    }

                    // Füge die eigene Position an den Anfang der Liste
                    // Das ist wichtig, damit die Linien richtig verbunden werden.
                    positions.splice(0, 0, new LatLng(lat, lng));

                    // Eigene Position als Marker hinzufügen
                    const marker = new Marker([lat, lng], markerOptions).addTo(this.map);

                    this.markers.push(marker);
                }
            }

            // Verbinde die Positionen mit Linien
            if (positions.length > 1 && !this.locationOnly) {
                const polyline = new Polyline(positions, { color: 'blue', weight: 3 }).addTo(this.map);

                this.lines.push(polyline);
            }

            // Ansicht an die Positionen anpassen
            if (fitToBounds && positions.length) {
                const bounds = new LatLngBounds(positions);

                if (bounds.isValid()) {
                    this.map.fitBounds(bounds, {
                        maxZoom: 14,
                        padding: [20, 20], // Padding around the bounds
                        animate: true, // Animation for the fitBounds
                        duration: 0.5, // Dauer der Animation in Sekunden
                        // easeLinearity: 0.2 // Linearität der Animation
                    });
                }
            }
        } catch (err) {
            log.error('updateMap: ' + Utils.getErrorMessage(err));
            return;
        }
    }

    async ngOnDestroy() {
        this.subscriptions.forEach(p => p.unsubscribe());
        this.subscriptions = [];

        this.isVisible = false;
    }
}
